import {SearchOutlined} from '@ant-design/icons'
import {Avatar, Input, Modal, Select} from 'antd'
import classNames from 'classnames'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import LookupField from 'components/LookupField'
import Pure from 'components/Pure'
import Toggle from 'components/Toggle'
import {Formik} from 'formik'
import _ from 'lodash'
import {useAppConfig} from 'modules/local/AppConfigContext'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import AntdConfigWrapper, {RenderEmptyMeta,} from 'views/AppLocals/custom/AntdConfigWrapper'
import {reactSelectComponents, SelectWidget,} from 'views/AppLocals/custom/ReactSelectComponents'
import {ATable} from 'views/AppLocals/EntityList'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {mapEntityOptions} from 'views/AppLocals/factory/createSelectEntityProps'
import {deepTranslate, NullAsDefault, renderElse, renderIf, shallowDiff,} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {RelationshipStatus} from '../components/Codi/CodiDocumentSource'
import TooltipApp from "components/TooltipApp";

const DocumentSelect = ({name, value, onChange}) => {
  const t = useTranslate()
  return (
    <LookupField
      defaultValue={value}
      createIfNotExists={false}
      getValue={(value) => value?.label}
      apiPath="/lookup/document/get-by-document-no"
      placeholder={t('choose a book')}
      renderEnterButton={(onClick) => (
        <TooltipApp title={t('search')}>
          <SearchOutlined onClick={onClick}/>
        </TooltipApp>
      )}
      handleChange={({target}) => {
        onChange(createValue(name, target))
      }}
      renderOption={(o, i) => {
        return (
          <Translate key={i}>
            {
              t => (
                <React.Fragment>
                  {renderIf(
                    o.avatar,
                    <Avatar
                      src={o.avatar}
                      size={25}
                      className="flex flex-center background-200"
                    />
                  )}
                  <div className="flex flex-col flex-1 gap-1">
                    <span className="font-bold text-sm text-color-000 flex-1 leading-tight">{o.label}</span>
                    {(_.get(o, 'issued_org_name') || _.get(o, 'document_no')) && (
                      <div className="flex items-center gap-1 italic leading-snug">
                        <span className="font-medium text-2xs text-primary">{_.get(o, 'issued_org_name')}</span>
                        <span className="font-medium text-2xs text-color-200"><span
                          className="text-color-400">={` -  ${t('document no')} : `}</span>
                          {_.get(o, 'document_no')}</span>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              )
            }
          </Translate>
        )
      }}
    />
  )
}

const FilterSelectField = ({
                             name,
                             value,
                             onChange,
                             options,
                             isLoading,
                             className,
                             ...props
                           }) => (
  <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
    <SelectWidget
      value={_.find(options, {value}) ? value : undefined}
      showSearch={true}
      allowClear={true}
      onChange={(value) => {
        onChange(name, value)
      }}
      loading={!!isLoading}
      optionFilterProp="label"
      className={classNames('CenterSelector w-full', className)}
      options={!!isLoading ? [] : options}
      suffixIcon={
        !!isLoading ? undefined : <reactSelectComponents.DropdownIndicator/>
      }
      {...props}
    />
  </AntdConfigWrapper>
)

const BlockSettingFilters = ({
                               value: filter = {
                                 status: undefined,
                                 document_type_id: undefined,
                               },
                               onChange: setValues = NullAsDefault,
                               fieldNames = ['status', 'document_type_id'],
                             }) => {
  const t = useTranslate()

  const {document_types = []} = useAppConfig('document_types') ?? {}

  const options = useMemo(
    () => mapEntityOptions(Array.from(document_types ?? [])),
    [document_types]
  )

  const handleChange = useCallback(
    (name, value) => {
      setValues({
        ...filter,
        [name]: value,
      })
    },
    [filter]
  )

  const className = useMemo(() => {
    switch (fieldNames.length) {
      case 0:
      case 1:
        return 'grid-cols-1'
      case 2:
      case 4:
        return 'grid-cols-1 md:grid-cols-2'
      case 3:
      default:
        return 'grid-cols-1 md:grid-cols-3'
    }
  }, [fieldNames])

  return renderElse(
    _.isEmpty(fieldNames),
    <div className={classNames('grid gap-2 mt-2', className)}>
      {renderIf(
        fieldNames.includes('document_type_id'),
        <FilterSelectField
          options={options}
          className="w-full"
          name="document_type_id"
          value={filter.document_type_id ?? undefined}
          placeholder={t('document type')}
          onChange={(name, value) => {
            handleChange(name, value)
          }}
        />
      )}
      {renderIf(
        fieldNames.includes('status'),
        <FilterSelectField
          options={[
            RelationshipStatus.PENDING,
            RelationshipStatus.PROCESSING,
            RelationshipStatus.COMPLETED,
          ].map(deepTranslate(t))}
          className="w-full flex flex-1"
          name="status"
          value={filter.status ?? undefined}
          placeholder={t('status')}
          onChange={(name, value) => {
            handleChange(name, value)
          }}
        />
      )}
    </div>
  )
}

const formSchema = {
  title: {
    title: 'title',
    children: [
      {
        name: 'title',
        component: createControlledFormField({
          placeholder: 'title',
        }),
      },
    ],
  },
  description: {
    title: 'description',
    children: [
      {
        name: 'description',
        component: createControlledFormField({
          rows: 5,
          placeholder: 'description',
          Component: Input.TextArea,
        }),
      },
    ],
  },
  link_type: {
    title: 'link type',
    children: [
      {
        name: 'link_type',
        component: ({name, form, value, onChange}) => {
          const options = _.get(form, 'values.link_types')
          return (
            <Translate>
              {(t) => (
                <SelectWidget
                  value={value}
                  className="w-full"
                  placeholder={t('click to select')}
                  onChange={(value) => {
                    onChange(createValue(name, value))
                  }}>
                  {Array.from(options || []).map(({label, value}, index) => (
                    <Select.Option
                      key={index}
                      label={label}
                      value={value}>
                      {t(label)}
                    </Select.Option>
                  ))}
                </SelectWidget>
              )}
            </Translate>
          )
        },
      },
    ],
  },
  linked_document: {
    title: 'linked document',
    children: [
      {
        name: 'linked_document',
        component: ({name, value, onChange}) => (
          <DocumentSelect {...{name, value, onChange}} />
        ),
      },
    ],
  },
}

const CreateModal = ({item, params, onCancel = NullAsDefault, onSubmit = NullAsDefault}) => {
  const t = useTranslate()

  const validate = (values) => {
    let errors = {}

    const {link_type, linked_document} = values || {}

    if (_.isEmpty(link_type)) {
      errors.link_type = t('required field')
    }

    if (!linked_document?.id) {
      errors.linked_document = t('required field')
    }

    return errors
  }

  const initialValue = useMemo(() => {
    const {link_type, link_types} = params || {}
    return {...(item || {}), link_type, link_types}
  }, [item, params])

  return (
    <Formik
      validate={validate}
      validateOnMount={true}
      validateOnChange={true}
      initialValues={initialValue}
      onSubmit={(values) => {
        const {linked_document, link_types, ...changes} = shallowDiff(
          values,
          initialValue
        )
        onSubmit(
          _.omitBy(
            {
              ...changes,
              link_type: params?.link_type,
              linked_document_id: linked_document?.id,
            },
            _.isUndefined
          )
        )
      }}
      enableReinitialize={true}>
      <div className="flex flex-col space-y-2">
        <Pure>
          <FieldsFactory
            formSchema={[
              // formSchema.link_type,
              formSchema.linked_document,
            ]}
          />
        </Pure>
        <FormActionBar submitText="create"/>
      </div>
    </Formik>
  )
}

const SettingBlock = ({
                        deps,
                        query,
                        params,
                        values,
                        apiInfo,
                        onCreate,
                        onUpdate,
                        document_id,
                        Wrapper = 'div',
                        renderItem = NullAsDefault,
                      }) => {
  const t = useTranslate()

  const [keyword, setKeyword] = useState()

  const [filter, setFilter] = useState({})

  const onSearch = _.debounce((keyword) => {
    setKeyword(keyword)
  }, 500)

  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const onCancel = () => {
    setAction({})
  }

  const modal_content = useMemo(() => {
    const {type, value} = action || {}
    let title
    let Content = NullAsDefault
    let _params = {title: undefined}
    switch (type) {
      case CRUD.CREATE:
        title = (
          <div className="text-center font-bold text-color-000 uppercase tracking-wide">
            {t('create docs')} {t(params?.type)}
          </div>
        )
        _params = {
          onCancel,
          onSubmit: (values) => {
            onCreate(values)
            onCancel()
          },
        }
        Content = CreateModal
        break
      default:
        break
    }
    return (
      <Modal
        width={600}
        className="custom-modal"
        footer={null}
        title={title}
        onCancel={onCancel}
        open={type}
        destroyOnHidden={true}>
        <Content
          {..._params}
          params={{document_id, ...(params ?? {})}}
          initialValues={value ?? {}}
        />
      </Modal>
    )
  }, [params, action, document_id])

  const handleAction = (type, params) => {
    switch (type) {
      case CRUD.CREATE:
        setAction({
          type,
          value: params,
        })
        break
      case CRUD.UPDATE:
        onUpdate(params)
        break
      default:
        break
    }
    return null
  }
  return (
    <Wrapper className="flex flex-col space-y-2">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-color-300 uppercase">
          {t('document')} {t(params.type)}
        </div>
        <div
          style={{padding: '3px 12px'}}
          className="rounded-full bg-primary text-white text-sm font-medium cursor-pointer"
          onClick={() => {
            handleAction(CRUD.CREATE)
          }}>
          {t('create new')}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Input.Search
          name="keyword"
          autoComplete="off"
          allowClear={true}
          onChange={(event) => {
            onSearch(event?.target?.value)
          }}
          onSearch={onSearch}
          placeholder={t('keyword')}
        />
        <BlockSettingFilters
          value={filter}
          onChange={(values) => {
            setFilter(values)
          }}
          fieldNames={['status', 'document_type_id'].filter((name) =>
            values ? !_.has(values, name) : true
          )}
        />
      </div>
      <ATable
        size="small"
        bordered={false}
        apiInfo={apiInfo}
        showHeader={false}
        createQuery={() => query}
        dependencies={[...(deps ?? []), document_id]}
        columns={[
          {
            render: (__, item) => (
              <div className="py-2">{renderItem(item)}</div>
            ),
          },
        ]}
        rowKey={(record) => record?.id}
        className="Borderless custom-out-table"
        createValues={({pagination}) => ({
          keyword,
          ...(values ?? {}),
          ...(filter ?? {}),
          page: _.defaultTo(pagination?.current, 1),
        })}
      />
      {modal_content}
    </Wrapper>
  )
}

const createSettingBlock = ({
                              deps,
                              query,
                              values,
                              render,
                              params,
                              apiInfo,
                              onCreate,
                              onUpdate,
                              onDelete,
                              renderItem,
                              document_id,
                              ...props
                            }) => {
  const filter_actions = {
    status: 'status',
    sortBy: 'sort-by',
    keyword: 'keyword',
    document_type_id: 'document-type',
  }

  return (
    <Translate>
      {(t) => (
        <Toggle>
          {(isToggle, toggle) => (
            <div>
              {render({
                isToggle,
                toggle,
              })}
              {isToggle && (
                <Modal
                  open={isToggle}
                  onCancel={() => {
                    toggle(false)
                  }}
                  onOk={() => {
                    toggle(false)
                  }}
                  width={720}
                  className="custom-modal"
                  destroyOnHidden={true}
                  title={
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                      {t('document')} {t(params.type)}
                    </div>
                  }
                  cancelText={t('close')}
                  cancelButtonProps={{
                    className: 'button-rounded-lg no-border',
                  }}
                  okButtonProps={{hidden: true}}>
                  <SettingBlock
                    {...{
                      deps,
                      query,
                      values,
                      params,
                      apiInfo,
                      onCreate,
                      onUpdate,
                      onDelete,
                      renderItem,
                      document_id,
                      filter_actions,
                    }}
                    {...props}
                  />
                </Modal>
              )}
            </div>
          )}
        </Toggle>
      )}
    </Translate>
  )
}

export default createSettingBlock
